import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSystemStatus, setSystemStatusPolling } from "store/features/app/appSlice";
import { RootState } from "store/rootReducer";

import { getSystemStatus } from "api/systemStatus";

interface UseSystemStatusOptions {
  /** Polling interval in milliseconds (default: 5 minutes) */
  pollingInterval?: number;
  /** Whether to enable polling (default: true) */
  enabled?: boolean;
}

export function useSystemStatusRedux({
  pollingInterval = 5 * 60 * 1000, // 5 minutes
  enabled = true
}: UseSystemStatusOptions = {}) {
  const dispatch = useDispatch();
  const systemStatus = useSelector(
    (state: RootState) =>
      state.app.systemStatus || {
        currentStatus: null,
        lastChecked: null,
        isPolling: false,
        hasActiveIssue: false
      }
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkSystemStatus = async () => {
    if (!enabled) return;

    try {
      dispatch(setSystemStatusPolling(true));
      const status = await getSystemStatus();
      dispatch(setSystemStatus(status));
    } catch (error) {
      // Failed to check system status - log error for debugging
      // eslint-disable-next-line no-console
      console.error("Failed to check system status:", error);
    } finally {
      dispatch(setSystemStatusPolling(false));
    }
  };

  // Set up polling - only one instance should poll (typically App.tsx)
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Check immediately on mount
    checkSystemStatus();

    // Set up polling interval
    intervalRef.current = setInterval(checkSystemStatus, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, pollingInterval]);

  return {
    currentStatus: systemStatus.currentStatus,
    isPolling: systemStatus.isPolling,
    lastChecked: systemStatus.lastChecked,
    checkNow: checkSystemStatus,
    hasActiveIssue: systemStatus.hasActiveIssue
  };
}
